// Insider Web SDK — somente para o cliente Coral (o script #insider-queue
// só existe no index.template.html da academiacoral).
const INSIDER_ENABLED = process.env.PROJECT_FOLDER === 'academiacoral'

// Ambiente de DEV
// const INSIDER_TAG_SRC = 'https://coral2bruat.api.useinsider.com/ins.js?id=10014731'
// Ambiente de PROD
const INSIDER_TAG_SRC = 'https://coral2br.api.useinsider.com/ins.js?id=10014516'

// Assinatura do último user empilhado. O user é empilhado no bootstrap (para o
// snapshot do ins.js) e de novo a cada page view (selectMethodByUrl). Sem dedup
// isso gera um type:'user' duplicado já no primeiro load e um re-push a cada
// navegação. Só re-empilhamos quando os dados de fato mudam (ex.: login).
let lastUserSignature = null

// Chamado por src/boot/insider.js no boot do app: empilha o user completo e SÓ
// ENTÃO injeta o ins.js. Motivo: em modo de teste o SDK tira um SNAPSHOT da fila
// no momento em que o ins.js carrega, e o wizard de integração valida os "single
// types" (user/currency/language) SOMENTE contra esse snapshot — um push de user
// feito depois (ex.: no mounted da página) nunca é visto pelo validador
// ("type: 'user' isn't pushed to the InsiderQueue array"). Page types (home/
// category/other) são validados contra o array ao vivo, então podem vir depois.
export function bootstrapInsider(store) {
    if (!INSIDER_ENABLED) return
    try {
        window.InsiderQueue = window.InsiderQueue || []
        pushUserData(getUserFromStore(store))

        if (!document.getElementById('insider-tag')) {
            const tag = document.createElement('script')
            tag.id = 'insider-tag'
            tag.async = true
            tag.src = INSIDER_TAG_SRC
            document.head.appendChild(tag)
        }
    } catch (err) {
        console.error('[Insider] erro no bootstrap:', err)
    }
}

// Rotas cuja PÁGINA empilha a própria sequência no load (mounted/watch/beforeRouteUpdate).
// O afterEach global do router NÃO deve empilhar nessas rotas — o validador do
// Insider exige exatamente UM type:'init' por page view.
export const PAGE_TRACKED_ROUTES = [
    'home',
    'home_surveys',
    'courses',
    'trails',
    'course',
    'trail_course',
    'trail',
]

// Ponto de entrada único (páginas e router): empilha user → page → init.
export function trackPageView(store, route) {
    if (!INSIDER_ENABLED) return
    try {
        selectMethodByUrl(route, getUserFromStore(store))
    } catch (err) {
        console.error('[Insider] erro ao montar fila:', err)
    }
}

export function getUserFromStore(store) {
    return {
        user_id: store.getters['common/id'],
        email: store.getters['common/email'],
        user_fullname: store.getters['common/full_name'],
        document: store.getters['common/document'],
        phone_mobile_number: store.getters['common/phone_mobile_number'],
        email_unsubscribe: store.getters['common/email_unsubscribe'],
    }
}

export function selectMethodByUrl(to, user) {
    console.log('[Insider Debug] selectMethodByUrl chamada com:', { to: to?.path, name: to?.name, hasUser: !!user });
    window.InsiderQueue = window.InsiderQueue || [];

    // 1. User Data
    pushUserData(user);

    // 2. Page Type — mapeamento rota → page type do Insider:
    //    home → 'home' (homepage_view); listagens → 'category' (listing_page_view,
    //    breadcrumb obrigatório); leituras e demais páginas → 'other' (other_page_view).
    switch (to.name) {
        case 'home':
        case 'home_surveys':
            pushHomePage();
            break
        case 'courses':
            pushListingPage(['Treinamentos']);
            break
        case 'trails':
            pushListingPage(['Trilhas']);
            break
        case 'course':
        case 'trail_course':
            pushOtherPage(to, 'Treinamento');
            break
        case 'trail':
            pushOtherPage(to, 'Trilha');
            break
        default:
            pushOtherPage(to);
    }

    // 3. Init — sinaliza ao Insider Tag que os dados da página estão completos.
    // Deve ser o ÚLTIMO push, sempre depois de user/currency/page.
    window.InsiderQueue.push({
        type: 'init'
    });
    console.log('[Insider Debug] InsiderQueue atualizado (selectMethodByUrl):', JSON.parse(JSON.stringify(window.InsiderQueue)));
}

export function pushUserData(user) {
    console.log('[Insider Debug] pushUserData executando, user:', user);

    // Atributos default ficam na raiz; atributos custom (isDefault=No) vão em "custom".
    // Regra do parceiro (SKILL.md): omitir a chave quando não há valor — nunca enviar
    // identificador null/""/undefined. O CPF é custom → vai como "cpf" (o Insider
    // prefixa com c_ automaticamente, virando o system name c_cpf).
    const value = {
        "language": "pt_BR",
        "gdpr_optin": !user.email_unsubscribe,
        "email_optin": !user.email_unsubscribe,
        "sms_optin": !user.email_unsubscribe,
        "whatsapp_optin": !user.email_unsubscribe,
        "custom": {"id_academiacoral": user.user_id}
    };

    if (user) {
        // if (user.user_id) value.uuid = String(user.user_id);
        if (user.email) {
            value.email = String(user.email);
            // value.username = String(user.email);
        }
        // birthday NÃO é enviado por enquanto: o wizard de teste da Insider
        // (web-sdk.js v1.0.0) valida campos date chamando Insider.fns.isValidDate,
        // que não existe no ins.js deste partner → TypeError aborta a validação
        // inteira e o teste fica "Not Tested". Reintroduzir quando a Insider
        // corrigir o wizard (o SDK em si aceita datetime normalmente).
        if (user.user_fullname) {
            const nameParts = String(user.user_fullname).trim().split(' ');
            value.name = nameParts[0];
            if (nameParts.length > 1) {
                value.surname = nameParts.slice(1).join(' ');
            }
        }

        if (user.phone_mobile_number) {
            const digits = String(user.phone_mobile_number).replace(/\D/g, '');
            if (digits.length === 10 || digits.length === 11) {
                value.phone_number = '+55' + digits;
            } else if (digits.length > 0) {
                value.phone_number = '+' + digits;
            }
        }

        // CPF é o identificador prioridade 1 (system name c_cpf). Usuário sem
        // documento: omitir as chaves por completo (nunca null/"") — regra do
        // parceiro para identificador indisponível.
        if (user.document) {
            const cpf = String(user.document);
            // Unificação de identidade: o ins.js lê value.custom_identifiers e
            // prefixa c_ sozinho (vira o identificador c_cpf).
            // value.custom_identifiers = { "cpf": cpf };
            // Atributo custom de perfil: "cpf" sem prefixo (Insider adiciona c_).
            // "c_cpf" duplicado é só para o wizard de teste, que procura
            // literalmente custom['c_cpf'] no snapshot da fila; chaves não
            // mapeadas são descartadas pela validação de schema do tag.
            value.custom = { "cpf": cpf, "id_academiacoral": user.user_id };
        }
    }

    // Dedup: só empilha o user quando muda de fato. Evita o type:'user'
    // duplicado no primeiro load (bootstrap + primeiro trackPageView) e o
    // re-push redundante a cada troca de página — a identidade já persiste no
    // SDK. O snapshot do ins.js (validado pelo wizard) é seedado no bootstrap,
    // ANTES do ins.js carregar, então o dedup não afeta o teste de integração.
    const signature = JSON.stringify(value);
    if (signature === lastUserSignature) {
        console.log('[Insider Debug] pushUserData: user inalterado, pulando push duplicado');
        return;
    }
    lastUserSignature = signature;

    window.InsiderQueue.push({
        type: 'user',
        value
    });
}

export function pushHomePage() {
    console.log('[Insider Debug] pushHomePage executando');
    window.InsiderQueue.push({
        type: 'home',
        value: {
            "custom": {
                "home_page_version": "version1",
            },
        }
    });
}

// Listagens (Treinamentos/Trilhas) → listing_page_view; breadcrumb é obrigatório (Array).
export function pushListingPage(breadcrumb) {
    console.log('[Insider Debug] pushListingPage executando, breadcrumb:', breadcrumb);
    window.InsiderQueue.push({
        type: 'category',
        value: {
            "breadcrumb": breadcrumb,
        }
    });
}

export function pushOtherPage(to, name) {
    console.log('[Insider Debug] pushOtherPage executando, path:', to?.path, 'name:', name);
    window.InsiderQueue.push({
        type: 'other',
        value: {
            "name": (name || to.name) + " detail" || to.path || "other",
            "custom": {
                "url": to.path,
            }
        }
    });
}
